import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  useDeleteExternalConnection,
  useReadExternalConnection,
} from '@/hooks/externalConnectionsApi';
import { useToast } from '@/hooks/use-toast';
import { ExternalConnectionDto } from '@/lib/integrationTypes/externalConnectionTypes';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const replaceRegex = /( {2,})/;

export default function DeleteConnectionModel({
  id,
}: {
  id: string;
  deviceData: ExternalConnectionDto;
}) {
  const queryClient = useQueryClient();
  const { data: connectionData } = useReadExternalConnection({ id });
  const { mutateAsync: deleteAsync } = useDeleteExternalConnection({ id, queryClient });
  const [deleteText, setDeleteText] = useState('');
  const navigate = useNavigate();
  const { toast } = useToast();

  const regexedName = connectionData?.externalConnection?.name.trim().replace(replaceRegex, ' ');

  async function deleteDevice() {
    await deleteAsync()
      .then(() => {
        toast({ title: 'Deleted Device' });
        navigate('/connections');
      })
      .catch(() => {
        toast({ title: 'Error deleting device', variant: 'destructive' });
      });
  }

  console.log(`"delete ${connectionData?.externalConnection.name.trim()}"`);

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant={'destructive'}>Permanently Delete External Connection</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Are you absolutely sure?</DialogTitle>
          <DialogDescription>
            This action cannot be undone easily. Every device under this connection will be deleted.
            {connectionData === undefined || connectionData.devices.length != 0 ? (
              <>
                <hr className="my-5" />
                <p className="pt-5 text-red-500">
                  There {connectionData?.devices.length === 1 ? 'is' : 'are'}{' '}
                  {connectionData?.devices?.length ?? 'loading'} device
                  {connectionData?.devices.length === 1 ? undefined : 's'} still connected. This
                  will delete all connected devices
                </p>
                <p className="pt-5 text-red-500">Are you sure you want to delete {regexedName}</p>
              </>
            ) : undefined}
            <div className="h-5"></div>
            <Label htmlFor="confirm-delete">
              Type
              <span className="mx-1 rounded-sm bg-gray-800 px-1 py-1">delete {regexedName}</span>
              to confirm
            </Label>
            <Input
              id="confirm-delete"
              className="mt-5"
              value={deleteText}
              onChange={(e) => setDeleteText(e.target.value)}
            />
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex flex-row flex-wrap justify-between">
          <DialogClose asChild>
            <Button variant={'ghost'}>Cancel</Button>
          </DialogClose>
          <DialogClose asChild>
            <Button
              disabled={deleteText.trim() !== `delete ${regexedName}`}
              onClick={deleteDevice}
              variant={'destructive'}
            >
              Confirm
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
